<template>
  <div class="exportable-reports text-left">
    <h4>Rapoarte exportabile</h4>
    <div class="export-report-grid">
      <section class="card export-report" aria-labelledby="products-report-title">
        <div class="card-header">
          <span class="report-format">CSV</span>
          <h4 id="products-report-title">Toate produsele</h4>
          <p>Exportă catalogul de produse, cu categorii, secții, POS ID, TVA și prețuri. Include variantele.</p>
        </div>
        <div class="card-content">
          <div class="form-group">
            <label for="products-report-location">Locație</label>
            <select id="products-report-location" v-model="productLocationId" class="form-control">
              <option value="">Toate locațiile disponibile</option>
              <option v-for="location in locations" :key="location.true_id" :value="location.true_id">{{ location.name }}</option>
            </select>
          </div>
          <label class="active-products"><input v-model="activeOnly" type="checkbox"> Doar produsele active</label>
        </div>
        <div class="report-actions">
          <p v-if="error && errorReport === 'products'" class="text-danger" role="alert">{{ error }}</p>
          <button class="btn btn-info" :disabled="!!requesting || !!exporting" @click="download('products')">{{ exporting === 'products' ? 'Se exportă...' : 'Export CSV — toate produsele' }}</button>
          <div v-if="timedOut['products']" class="report-request-fallback">
            <p>Exportul direct nu a putut fi finalizat. Solicită raportul pentru a fi pregătit în fundal.</p>
            <button class="btn btn-info" :disabled="!!requesting || !!exporting" @click="requestReport('products')">{{ requesting === 'products' ? 'Se trimite...' : 'Solicită raport' }}</button>
          </div>
        </div>
      </section>

      <section class="card export-report" aria-labelledby="coupon-report-title">
        <div class="card-header">
          <span class="report-format">CSV</span>
          <h4 id="coupon-report-title">Comenzi cu cupon</h4>
          <p>Exportă comenzile care au folosit cuponul selectat, inclusiv codurile multiple. Include toate statusurile, fără ciorne.</p>
        </div>
        <div class="card-content">
          <div class="form-group">
            <label for="sql-report-coupon">Cupon</label>
            <el-select id="sql-report-coupon" v-model="couponId" filterable clearable
                       placeholder="Caută un cupon după nume sau cod"
                       :loading="loadingCoupons" :disabled="loadingCoupons"
                       no-match-text="Niciun cupon găsit" no-data-text="Nu există cupoane disponibile"
                       style="width: 100%;">
              <el-option v-for="coupon in coupons" :key="coupon.id" :value="coupon.id"
                         :label="couponLabel(coupon)"></el-option>
            </el-select>
            <p v-if="couponError" class="text-danger">{{ couponError }} <button class="btn btn-sm" @click="loadCoupons">Reîncearcă</button></p>
          </div>
          <div class="form-group">
            <label for="coupon-report-location">Locație</label>
            <select id="coupon-report-location" v-model="couponLocationId" class="form-control">
              <option value="">Toate locațiile disponibile</option>
              <option v-for="location in locations" :key="location.true_id" :value="location.true_id">{{ location.name }}</option>
            </select>
          </div>
          <div class="report-dates">
            <div class="form-group">
              <label for="sql-report-from">De la</label>
              <input id="sql-report-from" v-model="fromDate" type="date" class="form-control">
            </div>
            <div class="form-group">
              <label for="sql-report-to">Până la (inclusiv)</label>
              <input id="sql-report-to" v-model="toDate" type="date" class="form-control">
            </div>
          </div>
          <p class="text-muted">Perioada este opțională și se aplică datei adăugării comenzii.</p>
        </div>
        <div class="report-actions">
          <p v-if="error && errorReport === 'coupon-orders'" class="text-danger" role="alert">{{ error }}</p>
          <button v-if="!couponRequiresRequest" class="btn btn-info" :disabled="!!requesting || !!exporting || !validDates || !couponId" @click="download('coupon-orders')">{{ exporting === 'coupon-orders' ? 'Se exportă...' : 'Export CSV — comenzi cu cupon' }}</button>
          <div v-if="couponRequiresRequest || timedOut['coupon-orders']" class="report-request-fallback">
            <p v-if="couponRequiresRequest">Pentru perioade mai mari de o lună, raportul se pregătește în fundal. Îl vei găsi în Solicitări Rapoarte.</p>
            <p v-else>Exportul direct nu a putut fi finalizat. Solicită raportul pentru a fi pregătit în fundal.</p>
            <button class="btn btn-info" :disabled="!!requesting || !!exporting || !validDates || !couponId" @click="requestReport('coupon-orders')">{{ requesting === 'coupon-orders' ? 'Se trimite...' : 'Solicită raport' }}</button>
          </div>
        </div>
      </section>
    </div>
    <p class="report-request-info">
      Rapoartele solicitate sunt pregătite în fundal. Le vei găsi în tabul
      <button type="button" class="report-request-link" @click="$emit('show-requests')">Solicitări Rapoarte</button>,
      de unde le poți descărca după procesare.
    </p>
  </div>
</template>

<script>
import {Select, Option} from 'element-ui'
import {canRequestBackgroundReport, isLongReportPeriod} from './exportableReportHelpers'

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option
  },
  created () {
    this.loadCoupons()
  },
  data () {
    return {
      productLocationId: '',
      couponLocationId: '',
      activeOnly: false,
      fromDate: '',
      toDate: '',
      couponId: '',
      coupons: [],
      loadingCoupons: false,
      couponError: '',
      exporting: '',
      requesting: '',
      timedOut: {'products': null, 'coupon-orders': null},
      errorReport: '',
      error: ''
    }
  },
  watch: {
    productFilterKey () { this.timedOut.products = null },
    couponFilterKey () { this.timedOut['coupon-orders'] = null }
  },
  computed: {
    couponRequiresRequest () {
      return isLongReportPeriod(this.fromDate, this.toDate)
    },
    productFilterKey () { return JSON.stringify(this.buildParams('products')) },
    couponFilterKey () { return JSON.stringify(this.buildParams('coupon-orders')) },
    validDates () {
      return !this.fromDate || !this.toDate || this.fromDate <= this.toDate
    },
    locations () {
      const company = this.$session.get('selectedCompanyData') || {}
      let locations = company.locations_details || []
      if (!this.isOwner()) {
        const allowed = JSON.parse(this.$session.get('userDataLocations') || '[]').map(String)
        if (allowed.length) locations = locations.filter(location => allowed.includes(String(location.true_id)))
      }
      return locations
    }
  },
  methods: {
    buildParams (report) {
      return {
        company_id: this.$session.get('selectedCompany'),
        location_id: (report === 'products' ? this.productLocationId : this.couponLocationId) || null,
        coupon_id: report === 'coupon-orders' ? this.couponId : undefined,
        active_only: report === 'products' && this.activeOnly ? 1 : 0,
        from_date: report === 'products' ? undefined : (this.fromDate || undefined),
        to_date: report === 'products' ? undefined : (this.toDate || undefined)
      }
    },
    async requestReport (report) {
      if (report === 'coupon-orders' && (!this.validDates || !this.couponId)) return
      const params = report === 'coupon-orders' && this.couponRequiresRequest
        ? this.buildParams(report)
        : this.timedOut[report]
      if (!params || this.requesting || this.exporting) return
      this.requesting = report
      this.errorReport = report
      this.error = ''
      try {
        const response = await this.axios.post('/reports/exportable/' + report + '/request', params, {
          baseURL: process.env.API_BASE_URL,
          headers: {Authorization: 'Bearer ' + this.$session.get('token')},
          timeout: 30000
        })
        if (!response.data || response.data.status !== true) throw new Error('Request failed')
        this.timedOut[report] = null
        this.$emit('show-requests')
      } catch (error) {
        this.error = 'Solicitarea nu a putut fi confirmată. Încearcă din nou sau verifică tabul Solicitări Rapoarte.'
      } finally {
        this.requesting = ''
      }
    },
    couponLabel (coupon) {
      const code = coupon.coupon_has_multiple_codes ? 'coduri multiple' : coupon.coupon_code
      return (coupon.name || 'Cupon') + ' (#' + coupon.id + ')' +
        (code ? ' — ' + code : '') + (!coupon.active ? ' — inactiv' : '')
    },
    loadCoupons () {
      this.loadingCoupons = true
      this.couponError = ''
      this.apiRequest('/reports/exportable-coupons', 'GET', {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }, response => {
        this.loadingCoupons = false
        if (response.status === true) {
          this.coupons = response.data
        } else {
          this.couponError = 'Lista cupoanelor nu a putut fi încărcată.'
        }
      })
    },
    async download (report) {
      if (this.requesting || this.exporting || (report !== 'products' && (!this.validDates || !this.couponId))) return
      if (report === 'coupon-orders' && this.couponRequiresRequest) return
      const params = this.buildParams(report)
      this.timedOut[report] = null
      this.exporting = report
      this.errorReport = report
      this.error = ''
      try {
        const response = await this.axios.get('/reports/exportable/' + report, {
          baseURL: process.env.API_BASE_URL,
          headers: {Authorization: 'Bearer ' + this.$session.get('token')},
          responseType: 'blob',
          timeout: 180000,
          params
        })
        if (!(response.headers['content-type'] || '').includes('text/csv')) {
          const failure = new Error('Exportul nu a returnat un fișier CSV.')
          failure.response = response
          throw failure
        }
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = report + (report === 'coupon-orders' ? '-' + this.couponId : '') + '.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      } catch (error) {
        if (await canRequestBackgroundReport(error)) {
          if (JSON.stringify(params) === JSON.stringify(this.buildParams(report))) {
            this.timedOut[report] = params
          }
          this.error = 'Exportul direct a expirat sau conexiunea cu serverul a fost întreruptă.'
          return
        }
        this.error = 'Exportul nu a putut fi generat. Verifică filtrele și încearcă din nou.'
        if (error.response && error.response.status === 403) {
          this.error = 'Nu ai acces la compania sau locația selectată.'
        }
      } finally {
        this.exporting = ''
      }
    }
  }
}
</script>

<style scoped>
.report-request-info {
  margin: 18px 0 0;
  color: #777;
  font-size: 13px;
}
.report-request-link {
  border: 0;
  padding: 0;
  background: none;
  color: #31708f;
  text-decoration: underline;
}
.report-request-fallback {
  margin-top: 16px;
  padding: 14px;
  border-radius: 6px;
  background: #eef6fa;
}

.export-report-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}
.export-report {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-bottom: 0;
  border: 1px solid #ddd;
  border-radius: 8px;
}
.export-report .card-header {
  padding: 24px 24px 18px;
  border-bottom: 1px solid #eee;
}
.export-report .card-header h4 {
  margin: 0 0 12px;
}
.export-report .card-header p {
  margin-bottom: 0;
  color: #666;
}
.report-format {
  float: right;
  padding: 3px 8px;
  margin-left: 10px;
  border-radius: 4px;
  background: #eef6fa;
  color: #31708f;
  font-size: 12px;
  font-weight: 600;
}
.export-report .card-content {
  padding: 24px;
}
.active-products {
  display: flex;
  align-items: center;
  gap: 8px;
}
.report-dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.report-actions {
  margin-top: auto;
  padding: 18px 24px 24px;
}
.report-actions .btn {
  white-space: normal;
}
@media (max-width: 1100px) {
  .export-report-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 480px) {
  .report-dates {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/ExportableReports.vue